import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtDateTime } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail } from "lucide-react";

const NO_RESEND = ["otp", "password_reset"];
const canResend = (r) => (r.status === "failed" || r.status === "quota") && !NO_RESEND.includes(r.email_type) && !r.resent_at;
const isExpiring = (r) => (r.status === "failed" || r.status === "quota") && NO_RESEND.includes(r.email_type);
const scrollToRow = (id) => {
  if (!id) return;
  const el = document.getElementById(`emrow-${id}`);
  if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-emerald-400"); setTimeout(() => el.classList.remove("ring-2", "ring-emerald-400"), 1500); }
};

const humanType = (t) => (t || "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const cr = (tid) => (tid ? `CR-${String(tid).slice(0, 8).toUpperCase()}` : "");

const STATUS = {
  sent: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
  quota: "bg-amber-100 text-amber-700",
};
const STATUS_LABEL = { sent: "Sent", failed: "Failed", quota: "Quota exceeded" };
const rowCls = (s) => (s === "failed" ? "bg-red-50 hover:bg-red-100" : s === "quota" ? "bg-amber-50 hover:bg-amber-100" : "");

export const StatusBadge = ({ status }) => (
  <Badge className={`rounded-full ${STATUS[status] || "bg-slate-100 text-slate-700"}`} data-testid="email-status">{STATUS_LABEL[status] || status}</Badge>
);

// ---------------------------------------------------------------- View 1: Emails tab
export default function EmailsTab() {
  const [, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [resending, setResending] = useState(null);
  const [f, setF] = useState({ recipient: "", email_type: "all", status: "all", order_ref: "", date_from: "", date_to: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (f.recipient) params.recipient = f.recipient;
      if (f.email_type !== "all") params.email_type = f.email_type;
      if (f.status !== "all") params.status = f.status;
      if (f.order_ref) params.order_ref = f.order_ref;
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      const { data } = await api.get("/admin/emails", { params });
      setRows(data.emails || []);
      setTotal(data.total || 0);
      setTypes(data.types || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [f]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openOrder = (tid) => setSearchParams({ tab: "transactions", order: tid });
  const resend = async (r) => {
    setResending(r.id);
    try {
      const { data } = await api.post(`/admin/emails/${r.id}/resend`);
      if (data.status === "sent") toast.success("Email resent");
      else if (data.status === "quota") toast.error("Resend hit the sending quota — try again later");
      else toast.error(data.error || "Resend failed");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); }
    finally { setResending(null); }
  };
  const openResent = (r) => { const nr = rows.find((x) => x.id === r.resent_to_id); if (nr) setDetail(nr); };
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const clear = () => setF({ recipient: "", email_type: "all", status: "all", order_ref: "", date_from: "", date_to: "" });

  return (
    <div data-testid="admin-emails-tab">
      <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
        <Input data-testid="email-search" placeholder="Recipient…" value={f.recipient} onChange={(e) => set("recipient")(e.target.value)} className="rounded-lg" />
        <Select value={f.email_type} onValueChange={set("email_type")}>
          <SelectTrigger className="rounded-lg" data-testid="email-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {types.map((t) => <SelectItem key={t} value={t}>{humanType(t)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={f.status} onValueChange={set("status")}>
          <SelectTrigger className="rounded-lg" data-testid="email-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="quota">Quota exceeded</SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid="email-order-filter" placeholder="Order ref (full id)" value={f.order_ref} onChange={(e) => set("order_ref")(e.target.value)} className="rounded-lg" />
        <Input data-testid="email-date-from" type="date" value={f.date_from} onChange={(e) => set("date_from")(e.target.value)} className="rounded-lg" />
        <Input data-testid="email-date-to" type="date" value={f.date_to} onChange={(e) => set("date_to")(e.target.value)} className="rounded-lg" />
      </div>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs text-slate-500" data-testid="email-count">{loading ? "Loading…" : `${rows.length} shown · ${total} total`}</p>
        <Button variant="ghost" size="sm" className="rounded-full text-xs" data-testid="email-clear" onClick={clear}>Clear filters</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Date &amp; Time</TableHead><TableHead>Recipient</TableHead><TableHead>Type</TableHead><TableHead>Subject</TableHead><TableHead>Order</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={7} className="py-8 text-center text-slate-400">No emails logged.</TableCell></TableRow>}
          {rows.map((r) => (
            <TableRow key={r.id} data-testid="email-row" className={`cursor-pointer ${rowCls(r.status)}`} onClick={() => setDetail(r)}>
              <TableCell className="whitespace-nowrap text-xs text-slate-500">{fmtDateTime(r.ts)}</TableCell>
              <TableCell className="text-sm">{r.recipient}</TableCell>
              <TableCell><Badge variant="outline" className="rounded-full text-[11px]">{humanType(r.email_type)}</Badge></TableCell>
              <TableCell className="max-w-[260px] truncate text-sm text-slate-700">{r.subject}</TableCell>
              <TableCell className="font-mono text-xs">
                {r.order_ref
                  ? <button type="button" data-testid="email-order-link" className="text-primary hover:underline" onClick={(e) => { e.stopPropagation(); openOrder(r.order_ref); }}>{cr(r.order_ref)}</button>
                  : <span className="text-slate-300">—</span>}
              </TableCell>
              <TableCell><StatusBadge status={r.status} /></TableCell>
              <TableCell className="whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                {r.resent_at
                  ? <button type="button" data-testid="email-resent-badge" className="text-xs font-medium text-emerald-600 hover:underline" title="View the resent email" onClick={() => openResent(r)}>Resent ✓ · {fmtDateTime(r.resent_at)}</button>
                  : canResend(r)
                  ? <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" data-testid="email-resend-btn" disabled={resending === r.id} onClick={() => resend(r)}>{resending === r.id ? "Resending…" : "Resend"}</Button>
                  : isExpiring(r)
                  ? <span className="text-xs text-slate-400" data-testid="email-resend-expire-note">Ask the user to request a new one</span>
                  : <span className="text-slate-300">—</span>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-2xl" data-testid="email-detail-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4" /> Email record</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm text-slate-700">
              <Row label="Date & time" value={fmtDateTime(detail.ts)} />
              <Row label="Recipient" value={detail.recipient} />
              {detail.cc && <Row label="Cc" value={detail.cc} />}
              <Row label="Type" value={humanType(detail.email_type)} />
              <Row label="Subject" value={detail.subject} />
              <Row label="Status" value={<StatusBadge status={detail.status} />} />
              {detail.order_ref && <Row label="Order" value={<button type="button" data-testid="email-detail-order-link" className="font-mono text-primary hover:underline" onClick={() => { setDetail(null); openOrder(detail.order_ref); }}>{cr(detail.order_ref)}</button>} />}
              {detail.user_id && <Row label="User id" value={<span className="font-mono text-xs">{detail.user_id}</span>} />}
              {detail.error && <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700" data-testid="email-detail-error"><b>Error:</b> {detail.error}</div>}
              {detail.resent_at && <button type="button" data-testid="email-detail-resent" className="text-left text-xs font-medium text-emerald-600 hover:underline" onClick={() => openResent(detail)}>Resent ✓ · {fmtDateTime(detail.resent_at)} — view the resent email</button>}
              {canResend(detail) && <Button size="sm" variant="outline" className="rounded-full" data-testid="email-detail-resend-btn" disabled={resending === detail.id} onClick={() => resend(detail)}>{resending === detail.id ? "Resending…" : "Resend email"}</Button>}
              {isExpiring(detail) && <p className="text-xs text-slate-400" data-testid="email-detail-resend-note">This email can't be resent — ask the user to request a new one.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-1.5 last:border-0">
    <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-slate-400">{label}</span>
    <span className="text-right">{value}</span>
  </div>
);

// ---------------------------------------------------------------- View 2: emails for one order
export function OrderEmails({ orderRef }) {
  const [rows, setRows] = useState(null);
  const [resending, setResending] = useState(null);
  const load = useCallback(() => api.get("/admin/emails", { params: { order_ref: orderRef, limit: 200 } })
    .then(({ data }) => setRows([...(data.emails || [])].reverse())).catch(() => setRows([])), [orderRef]); // oldest-first
  useEffect(() => { load(); }, [load]);
  const resend = async (r) => {
    setResending(r.id);
    try {
      const { data } = await api.post(`/admin/emails/${r.id}/resend`);
      if (data.status === "sent") toast.success("Email resent");
      else if (data.status === "quota") toast.error("Resend hit the sending quota — try again later");
      else toast.error(data.error || "Resend failed");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); }
    finally { setResending(null); }
  };
  return (
    <div className="pt-1" data-testid="order-emails-section">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Emails</p>
      {rows === null ? <p className="text-xs text-slate-400">Loading…</p>
        : rows.length === 0 ? <p className="text-xs text-slate-400" data-testid="order-emails-empty">No emails logged for this order yet.</p>
        : <div className="space-y-2">{rows.map((r) => (
            <div key={r.id} id={`emrow-${r.id}`} data-testid="order-email-row" className={`flex items-center justify-between gap-3 rounded-xl border p-3 transition-shadow ${r.status === "failed" ? "border-red-200 bg-red-50" : r.status === "quota" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{humanType(r.email_type)}</p>
                <p className="truncate text-xs text-slate-500">{r.recipient} · {fmtDateTime(r.ts)}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <StatusBadge status={r.status} />
                {r.resent_at
                  ? <button type="button" data-testid="order-email-resent" className="text-xs font-medium text-emerald-600 hover:underline" onClick={() => scrollToRow(r.resent_to_id)}>Resent ✓ · {fmtDateTime(r.resent_at)}</button>
                  : canResend(r)
                  ? <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" data-testid="order-email-resend-btn" disabled={resending === r.id} onClick={() => resend(r)}>{resending === r.id ? "…" : "Resend"}</Button>
                  : isExpiring(r)
                  ? <span className="text-xs text-slate-400" data-testid="order-email-resend-note">Ask the user to request a new one</span>
                  : null}
              </div>
            </div>
          ))}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- View 3: emails to one user
export function UserEmails({ userId }) {
  const [rows, setRows] = useState(null);
  const [resending, setResending] = useState(null);
  const load = useCallback(() => api.get("/admin/emails", { params: { user_id: userId, limit: 200 } })
    .then(({ data }) => setRows(data.emails || [])).catch(() => setRows([])), [userId]);
  useEffect(() => { load(); }, [load]);
  const resend = async (r) => {
    setResending(r.id);
    try {
      const { data } = await api.post(`/admin/emails/${r.id}/resend`);
      if (data.status === "sent") toast.success("Email resent");
      else if (data.status === "quota") toast.error("Resend hit the sending quota — try again later");
      else toast.error(data.error || "Resend failed");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); }
    finally { setResending(null); }
  };
  return (
    <div className="mt-6" data-testid="user-emails-section">
      <h3 className="mb-2 font-heading font-semibold text-slate-900">Emails</h3>
      {rows === null ? <p className="text-sm text-slate-400">Loading…</p>
        : rows.length === 0 ? <p className="rounded-xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-400" data-testid="user-emails-empty">No emails sent to this user yet.</p>
        : <div className="space-y-2">{rows.map((r) => (
            <div key={r.id} id={`emrow-${r.id}`} data-testid="user-email-row" className={`flex items-center justify-between gap-3 rounded-xl border p-3 transition-shadow ${r.status === "failed" ? "border-red-200 bg-red-50" : r.status === "quota" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{r.subject}</p>
                <p className="truncate text-xs text-slate-500">{humanType(r.email_type)} · {fmtDateTime(r.ts)}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <StatusBadge status={r.status} />
                {r.resent_at
                  ? <button type="button" data-testid="user-email-resent" className="text-xs font-medium text-emerald-600 hover:underline" onClick={() => scrollToRow(r.resent_to_id)}>Resent ✓ · {fmtDateTime(r.resent_at)}</button>
                  : canResend(r)
                  ? <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" data-testid="user-email-resend-btn" disabled={resending === r.id} onClick={() => resend(r)}>{resending === r.id ? "…" : "Resend"}</Button>
                  : isExpiring(r)
                  ? <span className="text-xs text-slate-400" data-testid="user-email-resend-note">Ask the user to request a new one</span>
                  : null}
              </div>
            </div>
          ))}</div>}
    </div>
  );
}
